import { useId, useRef } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../Icon/Icon'
import { cx } from '../utils/cx'
import './PhotoAttach.css'

export interface AttachedPhoto {
  id: string
  /** URL (objectURL o remota) de la miniatura */
  url?: string
  /** Texto alternativo / etiqueta */
  label: string
}

export interface PhotoAttachProps {
  /** Etiqueta del grupo, p. ej. "Foto" */
  label: ReactNode
  /** Nota junto a la etiqueta, p. ej. "· opcional, ayuda a arreglarlo antes" */
  labelNote?: ReactNode
  photos: AttachedPhoto[]
  onAdd: (file: File) => void
  onRemove: (id: string) => void
  /** Máximo de fotos; alcanzado, se oculta "Añadir" */
  max?: number
  className?: string
}

/** Adjuntar foto (bundle B6): miniatura de 88 px con botón "quitar" de 32 px superpuesto + botón discontinuo "Añadir" con icono de cámara. */
export function PhotoAttach({ label, labelNote, photos, onAdd, onRemove, max = 3, className }: PhotoAttachProps) {
  const { t } = useTranslation()
  const uid = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const labelId = `hp-attach-${uid}-label`
  return (
    <div className={cx('hp-attach', className)} role="group" aria-labelledby={labelId}>
      <span id={labelId} className="hp-attach__label">
        {label}
        {labelNote && <span className="hp-attach__note"> {labelNote}</span>}
      </span>
      <div className="hp-attach__row">
        {photos.map((p) => (
          <div key={p.id} className="hp-attach__thumb">
            <div role="img" aria-label={p.label} className="hp-attach__thumb-img" style={p.url ? { backgroundImage: `url(${p.url})` } : undefined} />
            <button type="button" aria-label={`${t('v2.ds.photoAttach.quitar')}: ${p.label}`} onClick={() => onRemove(p.id)} className="hp-attach__remove">
              <Icon name="x" size={16} />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <>
            <button type="button" onClick={() => inputRef.current?.click()} className="hp-attach__add">
              <Icon name="camera" size={22} />
              {t('v2.ds.photoAttach.anadir')}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="visually-hidden"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) onAdd(file)
                e.target.value = ''
              }}
            />
          </>
        )}
      </div>
    </div>
  )
}
